<template>
  <div v-if="isSwiping && isHeaderSwipe" class="app-reload-page" :style="mainStyles">
    <template v-if="willReload">
      <SpinnerIcon class="app-reload-page__spinner" />
      <span>Reloading the page...</span>
    </template>
    <template v-else>
      <span>Pull down to reload</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { usePointerSwipe } from '@vueuse/core'

import { RELOAD_DISTANCE, START_DISTANCE, TOP_ZONE_HEIGHT } from './utils/const'
import { getSafeAreaTop } from './utils/helpers'

import SpinnerIcon from '@/assets/images/icons/spinner.svg'

const MOBILE_HEADER_CLASS = '.main-mobile-header'
const DESKTOP_HEADER_CLASS = '.main-desktop-header'

const isHeaderSwipe = ref(false)
const safeAreaTop = ref(0)

const safeTopZoneHeight = computed(() => {
  return TOP_ZONE_HEIGHT + safeAreaTop.value
})

const getActiveHeader = (): HTMLElement | null => {
  const mobileHeader = document.querySelector(MOBILE_HEADER_CLASS) as HTMLElement
  const desktopHeader = document.querySelector(DESKTOP_HEADER_CLASS) as HTMLElement

  return mobileHeader?.offsetParent ? mobileHeader : 
    desktopHeader?.offsetParent ? desktopHeader : null
}

const { isSwiping, distanceY } = usePointerSwipe(document.body, {
  threshold: START_DISTANCE,
  pointerTypes: ['touch', 'pen'],
  onSwipeStart: (e: PointerEvent) => {

    isHeaderSwipe.value = e.clientY <= safeTopZoneHeight.value
  },
})

const distance = computed(() => {
  if (!isHeaderSwipe.value) return 0
  const baseDistance = distanceY.value > 0 ? 0 : Math.abs(distanceY.value)

  return baseDistance > safeAreaTop.value ? baseDistance - safeAreaTop.value : 0
})

const mainStyles = computed(() => {
  return {
    height: `${distance.value}px`,
  }
})

const willReload = computed(() => distance.value > RELOAD_DISTANCE)

const setHeaderOffset = (value?: number) => {
  const activeHeader = getActiveHeader()
  if (!activeHeader) return

  if (value === undefined) {
    activeHeader.removeAttribute('style')
    return
  }

  activeHeader.setAttribute(
    'style',
    `padding-top:0.5rem;transform:translateY(${value}px)`,
  )
}

watch(isSwiping, value => {
  if (value) return
  setHeaderOffset()
  if (isHeaderSwipe.value && distance.value > RELOAD_DISTANCE) {
    window.location.reload()
  }
})

watch(distance, value => {
  if (!isSwiping.value || !isHeaderSwipe.value) return
  setHeaderOffset(value)
})

onMounted(() => safeAreaTop.value = getSafeAreaTop())
</script>

<style>
.app-reload-page {
  @apply flex items-center justify-center;
  @apply gap-2 pt-safe-offset-2 pb-2;
  @apply bg-primary;
  @apply text-indigo-400;
  @apply z-[10000];

  &__spinner {
    @apply size-7;
    @apply animate-spin;
  }
}
</style>
